import PSTableDataSource from './PSTableDataSource.js';

/**
 * Data source for handling directly provided array data in PS Table.
 *
 * PSTableDataSourceDirect processes arrays of data directly in the browser,
 * handling sorting, pagination, and filtering on the client side. This is
 * suitable for smaller datasets that can be loaded entirely into memory.
 *
 * @class PSTableDataSourceDirect
 * @extends PSTableDataSource
 * @example
 * // Use with an array of objects
 * const dataSource = new PSTableDataSourceDirect({
 *   data_source: [
 *     { id: 1, name: 'John', age: 30 },
 *     { id: 2, name: 'Jane', age: 25 }
 *   ],
 *   paging: pagingInstance,
 *   sorting: sortingInstance,
 *   filtering: filteringInstance
 * });
 */
class PSTableDataSourceDirect extends PSTableDataSource {
	/**
	 * Creates a new PSTableDataSourceDirect instance.
	 *
	 * @param {Object} options - Data source configuration options
	 * @param {Array<Object>} options.data_source - Array of data objects to display. This is the data that will be displayed in the table.
	 * @param {PSTablePaging} options.paging - The pagination instance for the ps-table component
	 * @param {PSTableSorting} options.sorting - The sorting instance for the ps-table component
	 * @param {PSTableFiltering} options.filtering - The filtering instance for the ps-table component
	 */
	constructor(options) {
		super(options);
		this.mode = 'Direct data';
	}

	/**
	 * Retrieves and processes data from the direct array source.
	 *
	 * Performs client-side sorting, pagination, and filtering on the data array.
	 * Sorting is done using JavaScript's sort method with null value handling.
	 * Pagination is implemented using array slicing.
	 * Filtering is not currently supported for direct data sources.
	 *
	 * @returns {Promise<Array<Object>>} A promise that resolves to the processed data array
	 */
	async select() {
		let rows = this.data_source;

		// Set sorting variables.
		let sortfield = this.sorting.sortfield();
		let sortorder = this.sorting.sortorder();
		if (sortfield?.length > 0)
		{
			rows = rows.sort((a, b) => {
				let aVal = a?.[sortfield] ?? null;
				let bVal = b?.[sortfield] ?? null;

				// Handle null values - put them last
				if (aVal === null && bVal === null) return 0;
				if (aVal === null) return sortorder === 'ASC' ? 1 : -1; // null goes last
				if (bVal === null) return sortorder === 'ASC' ? -1 : 1; // null goes last

				if (sortorder === 'ASC')
					return aVal > bVal ? 1 : -1;
				else
					return aVal < bVal ? 1 : -1;
			});
		}

		// Set paging variables.
		let page_size = this.paging.page_size();
		let page_number = Math.max(1, this.paging.page_number());
		let offset = (page_number - 1) * page_size;
		this.paging.total_records(rows.length);
		this.paging.offset(offset);
		rows = rows.slice(offset, offset + page_size);

		// Set filtering
		// TODO: Implement filtering based on standard filtering options

		// Return data
		return rows;
	}
}

export default PSTableDataSourceDirect;
